// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import heroSkinApi from './heroSkin.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchHeroSkinApi = createAsyncThunk(
    'itemSaga/heroSkin/fetchHeroSkinApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await heroSkinApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const toggleHeroSkinPublishedStateApi = createAsyncThunk(
    'itemSaga/heroSkin/toggleHeroSkinPublishedStateApi',
    async (row, { dispatch, rejectWithValue }) => {
        try {
            await heroSkinApi.update(row.id, {is_ready: !row.is_ready});
            return row.id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const createHeroSkinApi = createAsyncThunk(
    'itemSaga/heroSkin/createHeroSkinApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await heroSkinApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeHeroSkinApi = createAsyncThunk(
    'itemSaga/heroSkin/storeHeroSkinApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const heroSkin = await heroSkinApi.store(values);
            alertSwal('success', 'HeroSkin created successfully!');

            return heroSkin;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showHeroSkinApi = createAsyncThunk(
    'itemSaga/heroSkin/showHeroSkinApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await heroSkinApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateHeroSkinApi = createAsyncThunk(
    'itemSaga/heroSkin/updateHeroSkinApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await heroSkinApi.update(id, values);
            alertSwal('success', 'HeroSkin updated successfully!');

            const { ranks } = state.itemSaga.heroSkinMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteHeroSkinApi = createAsyncThunk('itemSaga/heroSkin/deleteHeroSkinApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await heroSkinApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const heroSkinSlice = createSlice({
    name: 'itemSaga/heroSkin',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedHeroSkin(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHeroSkinApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchHeroSkinApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleHeroSkinPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleHeroSkinPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createHeroSkinApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createHeroSkinApi.rejected, () => {
                //
            });

        builder
            .addCase(storeHeroSkinApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeHeroSkinApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showHeroSkinApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showHeroSkinApi.rejected, () => {
                //
            });

        builder
            .addCase(updateHeroSkinApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateHeroSkinApi.rejected, () => {
                //
            });

        builder.addCase(deleteHeroSkinApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedHeroSkin } = heroSkinSlice.actions;
